'use client';
import { useEffect, useRef } from 'react';

import { CACHE_TIME } from '@/const/cache';
import { sleep } from '@/util/misc';

const ABANDON_SPACING_MS = 10000;

/**
 * Posts the member to the Braze marketing abandon list. This mirrors the legacy
 * multi-page flow, which fires from step 1's submit: `POST /api/marketing/abandon`
 * **twice, 10s apart** — a documented Braze workaround where a single call does
 * not reliably register.
 */
async function postAbandon(email: string, optinPromotion: boolean) {
  const requestOptions = {
    method: 'POST',
    body: JSON.stringify({
      email,
      optinPromotion: optinPromotion ? 'opted_in' : 'unsubscribed'
    }),
    next: { revalidate: CACHE_TIME.NO_CACHE }
  };

  try {
    await fetch('/api/marketing/abandon', requestOptions);
    await sleep(ABANDON_SPACING_MS);
    await fetch('/api/marketing/abandon', requestOptions);
  } catch {
    console.error('Error when adding user to marketing AA list');
  }
}

type UseMarketingAbandonParams = {
  /** Whether the "My Info" step's required fields all validate. */
  infoComplete: boolean;
  email: string;
  optinPromotion: boolean;
  /**
   * Suppress the fire — set when the session was rehydrated from persistence, so
   * a returning member whose info auto-completes on load does not re-fire. This
   * covers the case `completeAtMount` misses: rehydration fills the fields in an
   * effect *after* first render, which would otherwise look like a fresh
   * completion transition.
   */
  suppressed?: boolean;
};

/**
 * Re-anchors the legacy step-1 Braze abandon to the single-page flow: it fires
 * once, when the **info** step first completes — not on every re-validation,
 * which is what would otherwise duplicate it.
 */
export function useMarketingAbandon(params: UseMarketingAbandonParams) {
  const { infoComplete, email, optinPromotion, suppressed = false } = params;

  const firedRef = useRef(false);
  // Captured at first render: a session that mounts already complete must not
  // fire.
  const completeAtMountRef = useRef(infoComplete);

  useEffect(() => {
    if (
      infoComplete &&
      email &&
      !suppressed &&
      !firedRef.current &&
      !completeAtMountRef.current
    ) {
      firedRef.current = true;
      postAbandon(email, optinPromotion);
    }
  }, [infoComplete, email, optinPromotion, suppressed]);
}

export default useMarketingAbandon;
